import { Box, Typography } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMCountCategoryDatum {
  /** Main label for this row */
  label: string;
  /** Numeric count value to display */
  count: number;
  /** Dot color */
  color: string;
}

export interface PMCountCategoriesCardProps extends Omit<
  PMBaseCardProps,
  "children"
> {
  /** Array of up to three category items */
  data: PMCountCategoryDatum[];
  /** Card header props (icon, title, etc.)... */
}

/**
 * Category count card with up to three dot+count rows.
 *
 * @component
 * @param {PMCountCategoryDatum[]} data  Array of up to three category items to display (each with `label`, `count`, and `color`)
 * @param {React.ReactNode|string} [icon]  Icon for the card header (default "Star")
 * @param {string|React.ReactNode} [title] Card title (default "Title")
 * @param {boolean} [showInfoIcon]         Whether to show the info ("i") icon in the header (default: true)
 * @param {boolean} [showMenuIcon]         Whether to show the menu ("⋮") icon in the header (default: true)
 * @param {number|string} [fixedWidth]     Fixed width of the card (default "100%")
 * @param {number|string} [fixedHeight]    Fixed height of the card (default "auto")
 * @param {...PMBaseCardProps} rest        Any additional PMBaseCard props (e.g., sx, event handlers)
 */
export const PMCountCategoriesCard: PimoReactComponent<
  PMCountCategoriesCardProps
> = ({
  data,
  icon = "DonutLarge",
  title = "Title",
  showInfoIcon = true,
  showMenuIcon = true,
  fixedWidth = "100%",
  fixedHeight = "auto",
  ...rest
}) => {
  const visibleData = data.slice(0, 3);

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedWidth={fixedWidth}
      fixedHeight={fixedHeight}
      {...rest}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {visibleData.map((item, idx) => (
          <Box
            key={item.label}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: idx < visibleData.length - 1 ? 0.25 : 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  bgcolor: item.color,
                  mr: 1,
                }}
              />
              <Typography variant="body2" color="text.primary">
                {item.label}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.primary">
              {item.count}
            </Typography>
          </Box>
        ))}
      </Box>
    </PMBaseCard>
  );
};

export default PMCountCategoriesCard;
